import { sampleStreamTimeline } from '@/lib/stream-sampling';

type StreamsData = {
  time?: unknown;
  route_points?: unknown;
  elevation?: unknown;
  pace?: unknown;
  heartrate?: unknown;
  power?: unknown;
  cadence?: unknown;
} | undefined;

export function processActivityStreamsForCharts(streamsData: StreamsData) {
  if (!streamsData || !Array.isArray(streamsData.time) || streamsData.time.length === 0) {
    return [];
  }

  const timeArray = streamsData.time;
  const heartrate = Array.isArray(streamsData.heartrate) ? streamsData.heartrate : undefined;
  const paceStream = Array.isArray(streamsData.pace) ? streamsData.pace : undefined;
  const powerStream = Array.isArray(streamsData.power) ? streamsData.power : undefined;
  const elevationStream = Array.isArray(streamsData.elevation) ? streamsData.elevation : undefined;
  const cadenceStream = Array.isArray(streamsData.cadence) ? streamsData.cadence : undefined;

  const maxPoints = Math.min(timeArray.length, 200);

  const hasHeartRate = !!heartrate?.length;
  const hasPace = !!paceStream?.length;
  const hasPower = !!powerStream?.length;
  const hasElevation = !!elevationStream?.length;
  const hasCadence = !!cadenceStream?.length;

  return sampleStreamTimeline(timeArray, { maxPoints }).map((point) => {
    const actualIndex = point.index;

    const hrValue = hasHeartRate ? heartrate?.[actualIndex] : undefined;
    const heartRate = typeof hrValue === 'number' && Number.isFinite(hrValue) ? Math.round(hrValue) : undefined;

    let pace: number | undefined;
    if (hasPace) {
      const paceValue = paceStream?.[actualIndex];
      if (typeof paceValue === 'number' && Number.isFinite(paceValue) && paceValue > 0) {
        pace = paceValue;
      }
    }

    const powerValue = hasPower ? powerStream?.[actualIndex] : undefined;
    const power = typeof powerValue === 'number' && Number.isFinite(powerValue) ? Math.round(powerValue) : undefined;

    const elevationValue = hasElevation ? elevationStream?.[actualIndex] : undefined;
    const elevation = typeof elevationValue === 'number' && Number.isFinite(elevationValue) ? elevationValue : 0;

    const cadenceValue = hasCadence ? cadenceStream?.[actualIndex] : undefined;
    const cadence = typeof cadenceValue === 'number' && Number.isFinite(cadenceValue) ? Math.round(cadenceValue) : undefined;

    return {
      time: point.time,
      timeLabel: point.timeLabel,
      heartRate,
      pace: pace || undefined,
      power,
      elevation,
      cadence,
    };
  });
}

export function processActivityStreamsForSingleChart(
  streamsData: {
    time?: unknown[];
    pace?: unknown[];
    heartrate?: unknown[];
    elevation?: unknown[];
    power?: unknown[];
  },
  chartType: 'pace' | 'hr' | 'elevation' | 'power'
) {
  if (!streamsData || !Array.isArray(streamsData.time) || streamsData.time.length === 0) {
    return [];
  }

  const timeArray = streamsData.time;
  const maxPoints = Math.min(timeArray.length, 200);

  return sampleStreamTimeline(timeArray, { maxPoints })
    .map((point) => {
      const actualIndex = point.index;
      let value: number | undefined;

      if (chartType === 'pace') {
        const paceValue = Array.isArray(streamsData.pace) ? streamsData.pace[actualIndex] : undefined;
        if (typeof paceValue === 'number' && Number.isFinite(paceValue) && paceValue > 0) {
          value = paceValue;
        }
      } else if (chartType === 'hr') {
        const hrValue = Array.isArray(streamsData.heartrate) ? streamsData.heartrate[actualIndex] : undefined;
        if (typeof hrValue === 'number' && Number.isFinite(hrValue) && hrValue > 0) {
          value = Math.round(hrValue);
        }
      } else if (chartType === 'elevation') {
        const elevValue = Array.isArray(streamsData.elevation) ? streamsData.elevation[actualIndex] : undefined;
        if (typeof elevValue === 'number' && Number.isFinite(elevValue)) {
          value = elevValue;
        }
      } else if (chartType === 'power') {
        const powerValue = Array.isArray(streamsData.power) ? streamsData.power[actualIndex] : undefined;
        if (typeof powerValue === 'number' && Number.isFinite(powerValue) && powerValue > 0) {
          value = Math.round(powerValue);
        }
      }

      return {
        time: point.time,
        timeLabel: point.timeLabel,
        value,
      };
    })
    .filter((point) => point.value !== undefined);
}
